import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Modal, ModalTitle } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Input } from '../../../Input';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import toast from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { ModalBody } from 'design-system/src/components/Modal';
import { useProviders, useAddProviders } from '../../../../hooks/useProviders';
import { ModalFormRowWithButton, ModalAddButtonContainer } from '../../styles';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { InteractiveList } from '../../../InteractiveList/InteractiveList';

interface Provider {
  npi: string;
  firstName: string;
  lastName: string;
}

interface AddBatchProvidersModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  npi: string;
}

const AddProviderForm = ({
  onAdd,
  existingNpis,
  isAddingProvider,
}: {
  onAdd: (provider: Provider) => void;
  existingNpis: string[];
  isAddingProvider: boolean;
}) => {
  const [showErrors, setShowErrors] = useState(false);
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    clearErrors,
    setError,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      npi: '',
    },
  });
  const watchedValues = watch();
  const hasInput = watchedValues.npi?.trim();

  const onSubmit = (data: FormData) => {
    const trimmedNPI = data.npi.trim();
    if (existingNpis.includes(trimmedNPI)) {
      setError('npi', { message: 'This NPI has already been added' });
      setShowErrors(true);
      return;
    } else {
      onAdd({
        npi: trimmedNPI,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
      });
    }
    reset();
    clearErrors();
    setShowErrors(false);
  };

  const onError = () => {
    setShowErrors(true);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <ModalFormRowWithButton>
        <div className="min-w-0 flex-[2]">
          <Controller
            control={control}
            name="firstName"
            render={({ field }) => <Input type="text" label="First name" placeholder="First name" {...field} />}
          />
        </div>

        <div className="min-w-0 flex-[2]">
          <Controller
            control={control}
            name="lastName"
            render={({ field }) => <Input type="text" label="Last name" placeholder="Last name" {...field} />}
          />
        </div>

        <div className="min-w-0 flex-[3]">
          <Controller
            control={control}
            name="npi"
            rules={{
              required: { value: true, message: 'NPI is required' },
              pattern: {
                value: /^\d{10}$/,
                message: 'NPI must be exactly 10 digits',
              },
            }}
            render={({ field }) => (
              <Input
                type="text"
                inputMode="numeric"
                maxLength={10}
                label="NPI"
                placeholder="1234567890"
                hasError={!!(showErrors && errors.npi)}
                {...field}
              />
            )}
          />
          {showErrors && errors.npi && <ErrorMessage>{errors.npi.message}</ErrorMessage>}
        </div>

        <ModalAddButtonContainer>
          <Button type="submit" variant="primary" disabled={!hasInput} isLoading={isAddingProvider}>
            Add
          </Button>
        </ModalAddButtonContainer>
      </ModalFormRowWithButton>
    </form>
  );
};

const formatProviderName = (firstName: string | null, lastName: string | null) => {
  const providerName = [lastName, firstName].filter(Boolean).join(', ');
  return providerName || null;
};

const AddBatchProvidersModal: React.FC<AddBatchProvidersModalProps> = ({ open, onClose }) => {
  const { data: providersData, isLoading: isLoadingProviders } = useProviders();
  const providers = providersData?.providers ?? [];

  const { mutate: addProviders, isPending: isAddingProvider } = useAddProviders({
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Provider added</Toast>);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const handleAddProvider = (provider: Provider) => {
    addProviders({
      providers: [
        {
          npi: provider.npi,
          first_name: provider.firstName || undefined,
          last_name: provider.lastName || undefined,
        },
      ],
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Add providers</ModalTitle>
      <ModalBody>
        <AddProviderForm
          onAdd={handleAddProvider}
          existingNpis={providers.map((p) => p.npi)}
          isAddingProvider={isAddingProvider}
        />
        {isLoadingProviders ? (
          <LoadingSpinner height="200px" />
        ) : providers.length === 0 ? (
          <div className="flex min-h-[64px] items-center justify-center rounded-medium border border-solid border-grayscale-200">
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
              No providers have been added
            </Typography>
          </div>
        ) : (
          <div className="max-h-[356px] overflow-y-auto">
            <InteractiveList
              items={providers.map((provider) => {
                const name = formatProviderName(provider.first_name, provider.last_name);
                return {
                  id: provider.npi,
                  content: (
                    <>
                      {name && <Typography styledAs="bodySmallDMSans">{name}</Typography>}
                      <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
                        {provider.npi}
                      </Typography>
                    </>
                  ),
                  isInteractionDisabled: true,
                  disabledTooltip: 'Please go to the Providers tab on the Settings page to remove providers.',
                };
              })}
              onRemoveItem={() => {}}
              height="auto"
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AddBatchProvidersModal;
